import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Plus, Edit, Trash2, MapPin, Home, ArrowLeft, Shield } from 'lucide-react';
import { useFossasSepticas } from '../context/FossasSepticasProvider';
import { formatStatus } from '../utils/formatters';
import { toast } from 'react-hot-toast';
import supabase from '../../../config/supabaseClient';
import CadastroResidencia from './CadastroResidencia';
import { 
  getButtonStyle,
  primaryButtonStyle, 
  secondaryButtonStyle, 
  dangerButtonStyle, 
  applyIconOnlyStyle 
} from '../utils/buttonStyles';
import {
  getCardStyle,
  getCardGridStyle,
  cardTitleStyle,
  cardInfoLineStyle,
  cardSubtextStyle,
  getCardTitleColor
} from '../utils/cardStyles';
import {
  listContainerStyle,
  listItemStyle,
  listItemHeaderStyle,
  listItemTitleStyle,
  getStatusBadgeStyle,
  getInfoGridStyle,
  observationsContainerStyle,
  loadingIndicatorStyle,
  spinnerStyle,
  emptyListStyle,
  emptyListIconStyle,
  pageHeaderStyle,
  getInfoBadgeStyle,
  obraCardStyle,
  obraCardHeaderStyle,
  obraCardTitleStyle,
  obraCardStatusStyle,
  obraCardBodyStyle,
  obraCardInfoLineStyle,
  obraCardLabelStyle,
  obraCardValueStyle,
  obraCardFooterStyle
} from '../utils/listStyles';

/**
 * Componente que exibe a lista de residências de uma propriedade
 */
const ListaResidencias = () => {
  const { id: propriedadeId } = useParams();
  const navigate = useNavigate();
  const { 
    propriedades,
    residencias, 
    carregarPropriedades,
    carregarResidencias, 
    loading 
  } = useFossasSepticas();

  const [showCadastro, setShowCadastro] = useState(false);
  const [residenciaParaEditar, setResidenciaParaEditar] = useState(null);
  const [propriedade, setPropriedade] = useState(null);

  // Carregar dados ao montar o componente
  useEffect(() => {
    const fetchData = async () => {
      await carregarPropriedades();
      await carregarResidencias(parseInt(propriedadeId));
    };
    
    fetchData();
  }, [carregarPropriedades, carregarResidencias, propriedadeId]);

  // Atualizar propriedade quando os dados forem carregados
  useEffect(() => {
    if (propriedades.length > 0) {
      const prop = propriedades.find(p => p.id === parseInt(propriedadeId));
      if (prop) {
        setPropriedade(prop);
      } else {
        toast.error('Propriedade não encontrada');
        navigate('/fossas-septicas/propriedades');
      }
    }
  }, [propriedades, propriedadeId, navigate]);

  // Abrir formulário de cadastro
  const handleNovaCasa = () => {
    setResidenciaParaEditar(null);
    setShowCadastro(true);
  };

  // Abrir formulário de edição
  const handleEditar = (residencia) => {
    setResidenciaParaEditar(residencia);
    setShowCadastro(true);
  };

  // Fechar formulário
  const handleFecharCadastro = () => {
    setShowCadastro(false);
    setResidenciaParaEditar(null);
  };

  // Após salvar residência
  const handleSalvoComSucesso = () => {
    setShowCadastro(false);
    setResidenciaParaEditar(null);
    carregarResidencias(parseInt(propriedadeId));
  };

  // Excluir residência
  const handleExcluir = async (residenciaId) => {
    if (window.confirm('Tem certeza que deseja excluir esta residência? Esta ação não pode ser desfeita.')) {
      try {
        const { error } = await supabase
          .from('residencias')
          .delete()
          .eq('id', residenciaId);
        
        if (error) throw error;
        
        toast.success('Residência excluída com sucesso');
        carregarResidencias(parseInt(propriedadeId));
      } catch (error) {
        console.error('Erro ao excluir residência:', error);
        toast.error('Erro ao excluir residência');
      }
    }
  };

  if (loading.propriedades || !propriedade) {
    return (
      <div style={loadingIndicatorStyle}>
        <div style={spinnerStyle}></div>
        <p>Carregando dados da propriedade...</p>
      </div>
    );
  }

  return (
    <div className="card">
      {showCadastro ? (
        <CadastroResidencia 
          propriedadeId={parseInt(propriedadeId)}
          residencia={residenciaParaEditar}
          onClose={handleFecharCadastro}
          onSave={handleSalvoComSucesso}
        />
      ) : (
        <>
          <div style={{ ...pageHeaderStyle, alignItems: 'flex-start' }}>
            <div>
              <Link to="/fossas-septicas/propriedades" style={{ 
                display: 'inline-flex', 
                alignItems: 'center', 
                color: primaryButtonStyle.backgroundColor, 
                textDecoration: 'none',
                marginBottom: '10px',
                fontSize: '14px'
              }}>
                <ArrowLeft size={16} style={{ marginRight: '5px' }} />
                Voltar para Propriedades
              </Link>
              <h2 style={{ margin: '0 0 5px 0' }}>Residências da Propriedade: {propriedade.nome}</h2>
              <p style={{ color: '#666', margin: '0' }}>
                <strong>Proprietário:</strong> {propriedade.nome_proprietario} | 
                <strong> Localidade:</strong> {propriedade.localidade?.nome || 'Não definida'}
              </p>
            </div>
            <button 
              onClick={handleNovaCasa}
              style={getButtonStyle('primary')}
            >
              <Plus size={16} />
              Nova Residência
            </button>
          </div>

          {/* Cards de estatísticas */}
          <div style={getCardGridStyle(3)}>
            <div style={getCardStyle('success')}>
              <h3 style={{ ...cardTitleStyle, color: getCardTitleColor('success') }}>Total de Residências</h3>
              <div style={cardInfoLineStyle}>
                <span style={cardSubtextStyle}>Quantidade:</span>
                <strong>{residencias.length}</strong>
              </div>
              <div style={cardInfoLineStyle}>
                <span style={cardSubtextStyle}>Mapeadas:</span>
                <strong>{residencias.filter(r => r.status === 'Mapeada').length}</strong>
              </div>
              <div style={cardInfoLineStyle}>
                <span style={cardSubtextStyle}>Não Mapeadas:</span>
                <strong>{residencias.filter(r => r.status === 'Não Mapeada').length}</strong>
              </div>
            </div>
            
            <div style={getCardStyle('info')}>
              <h3 style={{ ...cardTitleStyle, color: getCardTitleColor('info') }}>Moradores</h3>
              <div style={cardInfoLineStyle}>
                <span style={cardSubtextStyle}>Total:</span>
                <strong>{residencias.reduce((total, r) => total + r.numero_moradores, 0)}</strong>
              </div>
              <div style={cardInfoLineStyle}>
                <span style={cardSubtextStyle}>Média por Residência:</span>
                <strong>
                  {residencias.length > 0 
                    ? (residencias.reduce((total, r) => total + r.numero_moradores, 0) / residencias.length).toFixed(1) 
                    : '0'}
                </strong>
              </div>
            </div>
            
            <div style={getCardStyle('danger')}>
              <h3 style={{ ...cardTitleStyle, color: getCardTitleColor('danger') }}>Fossas Sépticas</h3>
              <div style={cardInfoLineStyle}>
                <span style={cardSubtextStyle}>Total:</span>
                <strong>0</strong> {/* Aqui você pode adicionar a contagem real de fossas quando disponível */}
              </div>
              <div style={cardInfoLineStyle}>
                <span style={cardSubtextStyle}>Residências com Fossa:</span>
                <strong>0</strong> {/* Aqui você pode adicionar a contagem real quando disponível */}
              </div>
              <div style={cardInfoLineStyle}>
                <span style={cardSubtextStyle}>Residências sem Fossa:</span>
                <strong>{residencias.length}</strong> {/* Ajuste conforme necessário */}
              </div>
            </div>
          </div>

          {loading.residencias ? (
            <div style={loadingIndicatorStyle}>
              <div style={spinnerStyle}></div>
              <p>Carregando residências...</p>
            </div>
          ) : (
            <>
              {residencias.length === 0 ? (
                <div style={emptyListStyle}>
                  <Home size={48} style={emptyListIconStyle} />
                  <h3>Nenhuma residência cadastrada</h3>
                  <p>Esta propriedade ainda não possui residências cadastradas.</p>
                  <button 
                    onClick={handleNovaCasa}
                    style={{ ...getButtonStyle('primary'), marginTop: '15px' }}
                  >
                    <Plus size={16} />
                    Cadastrar Primeira Residência
                  </button>
                </div>
              ) : (
                <div style={listContainerStyle}>
                  {residencias.map(residencia => {
                    const statusInfo = formatStatus(residencia.status);
                    return (
                      <div key={residencia.id} style={obraCardStyle}>
                        {/* Cabeçalho do card */}
                        <div style={obraCardHeaderStyle}>
                          <h3 style={obraCardTitleStyle}>{residencia.identificacao}</h3>
                          <span style={{ 
                            ...obraCardStatusStyle, 
                            backgroundColor: statusInfo.color,
                            color: 'white'
                          }}>
                            {statusInfo.text}
                          </span>
                        </div>
                        
                        {/* Corpo do card */}
                        <div style={obraCardBodyStyle}>
                          <div style={obraCardInfoLineStyle}>
                            <span style={obraCardLabelStyle}>Moradores:</span>
                            <span style={obraCardValueStyle}>{residencia.numero_moradores}</span>
                          </div>
                          
                          <div style={obraCardInfoLineStyle}>
                            <span style={obraCardLabelStyle}>Coordenadas:</span>
                            <span style={obraCardValueStyle}>{residencia.latitude.toFixed(6)}, {residencia.longitude.toFixed(6)}</span>
                          </div>
                          
                          {residencia.observacoes && (
                            <div style={obraCardInfoLineStyle}>
                              <span style={obraCardLabelStyle}>Observações:</span>
                              <span style={obraCardValueStyle}>{residencia.observacoes}</span>
                            </div>
                          )}
                        </div>
                        
                        {/* Rodapé do card com botões */}
                        <div style={obraCardFooterStyle}>
                          <button 
                            style={applyIconOnlyStyle(getButtonStyle('secondary', { small: true }))}
                            onClick={() => handleEditar(residencia)}
                            title="Editar"
                          >
                            <Edit size={14} />
                          </button>
                          <button 
                            style={applyIconOnlyStyle(getButtonStyle('danger', { small: true }))}
                            onClick={() => handleExcluir(residencia.id)}
                            title="Excluir"
                          >
                            <Trash2 size={14} />
                          </button>
                          <Link 
                            to={`/fossas-septicas/fossas?residencia=${residencia.id}`} 
                            style={getButtonStyle('info', { small: true })}
                            title="Ver Fossas Sépticas"
                          >
                            <Shield size={14} />
                            Fossas
                          </Link>
                          <Link 
                            to={`/fossas-septicas/fossas/cadastro/${residencia.id}`} 
                            style={getButtonStyle('primary', { small: true })}
                            title="Nova Fossa Séptica"
                          >
                            <Plus size={14} />
                            Nova Fossa
                          </Link>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </>
      )}
      
      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default ListaResidencias;